import { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { Controller, FieldError, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  UserProfileAttributeMetadata,
  UserRepresentation,
  getPersonalInfo,
  savePersonalInfo,
  useEnvironment,
} from '@keycloak/keycloak-account-ui';
import { setUserProfileServerError } from '@keycloak/keycloak-ui-shared';
import { Info } from '@mui/icons-material';
import {
  Alert,
  Box,
  InputAdornment,
  Snackbar,
  Typography,
} from '@mui/material';

import { Button } from '@/ui/Button';
import { FieldHelperText } from '@/ui/FieldHelperText';
import { TextField } from '@/ui/TextField';

import { useProfile } from './ProfileProvider';
import { Avatar } from './ui/Avatar';

type MappedRules = {
  required: boolean;
  minLength: any;
  maxLength: any;
  readOnly: boolean;
};

const includeFields = ['lastName', 'firstName', 'email', 'nickname'];

export default function ProfileForm({ isDesktop }: { isDesktop: boolean }) {
  const { t } = useTranslation('profileForm');
  const context = useEnvironment();
  const form = useForm<UserRepresentation>({
    mode: 'onChange',
    defaultValues: {
      attributes: { nickname: '' },
      lastName: '',
      firstName: '',
      email: '',
    },
  });
  const { handleSubmit, reset, setError, formState } = form;

  const { setProfile } = useProfile();

  const [isSuccessSnackbarOpen, setSuccessSnackbarOpen] = useState(false);

  const [formConfig, setFormConfig] = useState<UserProfileAttributeMetadata[]>(
    [],
  );

  useEffect(() => {
    getPersonalInfo({ context }).then(({ attributes, ...restProfile }) => {
      const remappedAttributes =
        attributes &&
        Object.keys(attributes as { [key: string]: any[] }).reduce(
          (acc, key) => {
            return {
              ...acc,
              [key]: attributes[key][0],
            };
          },
          {},
        );

      const profile = {
        ...restProfile,
        attributes: remappedAttributes,
      };

      reset(profile);
      setProfile(profile);
      setFormConfig(profile.userProfileMetadata.attributes);
    });
  }, []);

  const onSubmit = async (user: UserRepresentation) => {
    try {
      await savePersonalInfo(context, user)
        .then(() => {
          setSuccessSnackbarOpen(true);
        })
        .catch((errors) =>
          setUserProfileServerError(
            { responseData: { errors } },
            (name, error) => setError(name, error),
            ((key) => t(`errors.${key}`)) as TFunction,
          ),
        );

      await context.keycloak.updateToken();
    } catch (error) {}
  };

  const onAccountDelete = () => {
    context.keycloak.login({
      action: 'delete_account',
    });
  };

  const getDefaultMinMax = (minLength: number, maxLength: number) => {
    return {
      minLength: {
        value: minLength || 2,
        message: t('errors.minLength', { count: minLength || 2 }),
      },
      maxLength: {
        value: maxLength || 255,
        message: t('errors.maxLength'),
      },
    };
  };

  const getEmailField = (rules: MappedRules) => (
    <Controller
      key="email"
      name="email"
      disabled={rules.readOnly}
      rules={{
        ...getDefaultMinMax(rules.minLength, rules.maxLength),
        required: rules.required && t('errors.required'),
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
          message: t('errors.error-invalid-email'),
        },
      }}
      render={({ field }) => (
        <TextField
          {...field}
          fullWidth
          slotProps={{
            inputLabel: { shrink: Boolean(field.value) },
            input: formState.errors.email && {
              endAdornment: (
                <InputAdornment position="start">
                  <Info color="error" />
                </InputAdornment>
              ),
            },
          }}
          margin="normal"
          label={t('fields.email')}
          error={Boolean(formState.errors.email)}
          helperText={
            formState.errors.email?.message && (
              <FieldHelperText>
                {formState.errors.email.message.toString()}
              </FieldHelperText>
            )
          }
        />
      )}
    />
  );

  const nicknameError = (
    formState.errors.attributes as { nickname?: FieldError }
  )?.nickname;

  const getUsernameField = (rules: MappedRules) => (
    <Controller
      key="nickname"
      name="attributes.nickname"
      disabled={rules.readOnly}
      rules={{
        ...getDefaultMinMax(rules.minLength, rules.maxLength),
        required: rules.required && t('errors.required'),
      }}
      render={({ field }) => (
        <TextField
          {...field}
          error={Boolean(nicknameError)}
          fullWidth
          margin="normal"
          label={t('fields.username')}
          slotProps={{
            inputLabel: { shrink: Boolean(field.value) },
          }}
          helperText={
            nicknameError?.message ? (
              <FieldHelperText>
                {nicknameError.message.toString()}
              </FieldHelperText>
            ) : (
              <FieldHelperText>{t('helperText.username')}</FieldHelperText>
            )
          }
        />
      )}
    />
  );

  return (
    <>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSuccessSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSuccessSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ borderRadius: '12px' }}
        >
          {t('snackbar.success')}
        </Alert>
      </Snackbar>
      <Typography
        mt={1}
        fontWeight={{ xs: 800, sm: 400 }}
        fontSize={{ xs: 24, sm: 36 }}
        variant="h4"
      >
        {t('title')}
      </Typography>
      <Box
        sx={{
          position: isDesktop ? 'absolute' : 'initial',
          top: { sm: 116, md: 132 },
          marginTop: !isDesktop ? '32px' : 0,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Avatar size="large" />
      </Box>
      <Box
        sx={{
          maxWidth: { xs: 'inherit', md: 380 },
          margin: 'auto',
          padding: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100% - 24px)',
        }}
      >
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              width: '100%',
            }}
          >
            <Typography
              mt={{ sm: 4, md: 6 }}
              fontSize={{ xs: 16, sm: 22 }}
              variant="h5"
              gutterBottom
              fontWeight={{ xs: 500, sm: 500 }}
            >
              {t('subtitle')}
            </Typography>
            {formConfig.map((fieldConfig) => {
              const mappedRules = {
                required: fieldConfig.required,
                minLength: fieldConfig.validators.length?.min,
                maxLength: fieldConfig.validators.length?.max,
                readOnly: fieldConfig.readOnly,
              };

              if (!includeFields.includes(fieldConfig.name)) {
                return;
              }

              if (fieldConfig.name === 'email') {
                return getEmailField(mappedRules);
              }

              if (fieldConfig.name === 'nickname') {
                return getUsernameField(mappedRules);
              }

              const fieldError = formState.errors[fieldConfig.name];

              return (
                <Controller
                  key={fieldConfig.name}
                  name={fieldConfig.name}
                  rules={{
                    ...getDefaultMinMax(
                      mappedRules.minLength,
                      mappedRules.maxLength,
                    ),
                    required: mappedRules.required && t('errors.required'),
                  }}
                  disabled={mappedRules.readOnly}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      error={Boolean(formState.errors[fieldConfig.name])}
                      fullWidth
                      margin="normal"
                      label={t(`fields.${fieldConfig.name}`)}
                      slotProps={{
                        inputLabel: { shrink: Boolean(field.value) },
                      }}
                      helperText={
                        fieldError?.message && (
                          <FieldHelperText>
                            {fieldError.message.toString()}
                          </FieldHelperText>
                        )
                      }
                    />
                  )}
                />
              );
            })}
            <Button
              type="submit"
              variant="outlined"
              color="primary"
              fullWidth
              sx={{ mt: 1 }}
            >
              {t('submitButton')}
            </Button>
          </form>
          <Button
            variant="text"
            color="error"
            fullWidth
            onClick={onAccountDelete}
            sx={{
              marginTop: isDesktop ? 'auto' : 4,
              marginBottom: 3,
            }}
          >
            {t('removeAccount')}
          </Button>
        </FormProvider>
      </Box>
    </>
  );
}
